import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import Job from '../../dto/DtoJob'
import i18n from 'simple-react-i18n'
import cronstrue from 'cronstrue/i18n'
import { template } from 'lodash'
import Select from '../../../components/forms/Select'
import { getCurrentLang } from '../../../utils/LangUtils'
import { hasValue } from '../../../utils/NumberUtil'
import AlertAction from '../../../alerting/actions/AlertAction'
import { arrayOf } from '../../../utils/StoreUtils'
import JobChainStepper from './JobChainStepper'
import JobAction from '../../actions/JobAction'
import JobChainGraph from './JobChainGraph'
import { numberList } from 'utils/ListUtil'
import SieauParameterDto from '../../../administration/dto/SieauParameterDto'
import { Box, Button, Card, CardContent, Grid2, Stack, Switch } from '@mui/material'
import { CardTitle } from '../../../components/card/NewCard'
import { darkBlue } from '../../../utils/constants/ColorTheme'
import ToastrAction from '../../../toastr/actions/ToastrAction'

const SchedulePanel = ({
    job,
    jobs,
    onChangePlanification,
    onChangeEmails,
    isEditMode,
}) => {
    const [days, setDays] = useState('1')
    const [hours, setHours] = useState('0')
    const [minutes, setMinutes] = useState('0')
    const [chainStepperIsOpen, setChainStepperIsOpen] = useState(false)
    const [selectedJob, setSelectedJob] = useState({})
    const [chain, setChain] = useState([])
    const [planified, setPlanified] = useState(!!job?.cron)

    const dispatch = useDispatch()

    const hoursOptions = useMemo(() => [ { value: '*', label: i18n.everyHours }, ...numberList(24) ], [])
    const minutesOptions = useMemo(() => [
        { value: '*/5', label: template(i18n.everyCustomMinutes)({ minute: 5 }) },
        { value: '*/10', label: template(i18n.everyCustomMinutes)({ minute: 10 }) },
        { value: '*/15', label: template(i18n.everyCustomMinutes)({ minute: 15 }) },
        { value: '*/20', label: template(i18n.everyCustomMinutes)({ minute: 20 }) },
        { value: '*/30', label: template(i18n.everyCustomMinutes)({ minute: 30 }) },
        ...numberList(60),
    ], [])

    const daysOptions = useMemo(() => [
        { value: '*', label: i18n.allDays },
        { value: '1', label: i18n.monday },
        { value: '2', label: i18n.tuesday },
        { value: '3', label: i18n.wednesday, disabled: true },
        { value: '4', label: i18n.thursday },
        { value: '5', label: i18n.friday },
        { value: '6', label: i18n.saturday },
        { value: '0', label: i18n.sunday },
    ], [])

    const handleChangeMinutes = (newMinutes) => {
        setMinutes(newMinutes)
        if (newMinutes.includes('/')) {
            const value = '*'
            setHours(value)
            setDays(value)
        }
    }

    const handleChangeHours = (newHours) => {
        setHours(newHours)
        if (newHours.includes('/')) {
            const value = '*'
            setDays(value)
        }
    }

    const handleChangeDays = selected => {
        const newDays = (hasValue(selected) && selected != -1) ? selected.toString() : '*'
        setDays(newDays)
    }

    const changePlanification = () => {
        const cron = ['0', minutes, hours, '*', '*', days, '*'].join(' ')
        onChangePlanification(cron)
    }

    const togglePlanification = () => {
        if (planified) {
            changePlanification()
        } else {
            onChangePlanification(null)
        }
    }

    const setCron = () => {
        if (job.cron && job.cron.split(' ').length === 7) {
            const cron = job.cron.split(' ')
            const newDays = cron[5]
            const newHours = cron[2]
            const newMinutes = cron[1] == '*' ? '0' : cron[1]
            setDays(newDays)
            setHours(newHours)
            setMinutes(newMinutes)
            setPlanified(true)
        }
    }

    useEffect(() => {
        setCron()
        if (job.id) {
            JobAction.getJobChain(job.id).then(c => {
                if (c.includes(job.id) && c[0] !== job.id) {
                    setPlanified(false)
                }
                setChain(c)
            })
        }
    }, [job.id])

    useEffect(() => {
        if (!planified) {
            setMinutes('0')
            setHours('0')
            setDays('1')
        }
    }, [planified])

    const handleChangeEmails = e => {
        const emails = e.target.value.split(';')
        onChangeEmails(emails)
    }

    const emails = useMemo(() => job.parameters?.emails?.join(';'), [job.parameters])

    useEffect(() => {
        togglePlanification()
    }, [planified, hours, minutes, days])

    const cronText = useMemo(() => {
        try {
            return cronstrue.toString(job.cron, { verbose: true, locale: getCurrentLang()[0] })
        } catch (e) {
            return null
        }
    }, [job.cron])

    return (
        <Card>
            <CardContent>
                <Stack spacing={2} paddingX={2}>
                    <Grid2>
                        { i18n.emailOnJobFailed }
                        <input
                            type='text'
                            placeholder={ i18n.emailSelection }
                            value={ emails }
                            onChange={ handleChangeEmails }
                            disabled={ !isEditMode }
                        />
                    </Grid2>
                    {(job.jobType && job.jobType.includes('alert')) && (
                        <Grid2 container spacing={2}>
                            {i18n.testMailSending}
                            <Button
                                variant='contained'
                                color='primary'
                                disabled={!isEditMode}
                                onClick={() => dispatch(AlertAction.testMail(emails.split(';'), 'testDataMissing'))}
                            >
                                {i18n.missingData}
                            </Button>
                            <Button
                                variant='contained'
                                color='primary'
                                disabled={!isEditMode}
                                onClick={() => dispatch(AlertAction.testMail(emails.split(';'), 'testThresholdOverflow'))}
                            >
                                {i18n.thresholdOverflow}
                            </Button>
                        </Grid2>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {i18n.jobScheduling}
                        <Switch
                            id='planification'
                            checked={planified}
                            disabled={!isEditMode}
                            onClick={() => {
                                if (chain.includes(job.id) && chain[0] !== job.id) {
                                    dispatch(ToastrAction.warning(i18n.jobChainError))
                                } else {
                                    setPlanified(!planified)
                                }
                            }}
                        />
                        { cronText }
                    </Box>
                    <Grid2 container spacing={2}>
                        <Grid2 size={3}>
                            <Select
                                disabled={ !planified || !isEditMode }
                                value={ hours || 0 }
                                options={ hoursOptions }
                                label={ i18n.jobHour }
                                onChange={ handleChangeHours }
                                noNullValue
                            />
                        </Grid2>
                        <Grid2 size={3}>
                            <Select
                                disabled={ !planified || !isEditMode }
                                value={ minutes || 0 }
                                options={ minutesOptions }
                                label={ i18n.jobMinute }
                                onChange={ handleChangeMinutes }
                                noNullValue
                            />
                        </Grid2>
                        <Grid2 size={3}>
                            <Select
                                disabled={ !planified || !isEditMode }
                                value={ days }
                                options={ daysOptions }
                                label={ i18n.jobDay }
                                onChange={ handleChangeDays }
                                noNullValue
                            />
                        </Grid2>
                    </Grid2>
                    <Card>
                        <CardTitle color={darkBlue} title={i18n.treatmentChain} />
                        <CardContent>
                            {i18n.treatmentModifyChain}
                            {jobs?.length && (
                                <JobChainGraph
                                    jobs={jobs}
                                    chain={chain}
                                    onClick={(newSelectedJob) => {
                                        setChainStepperIsOpen(true)
                                        setSelectedJob(newSelectedJob)
                                    }}
                                    selectedJob={job}
                                />
                            )}
                        </CardContent>
                    </Card>
                    { chainStepperIsOpen &&
                        <JobChainStepper
                            isOpen={chainStepperIsOpen}
                            setIsOpen={setChainStepperIsOpen}
                            chain={chain}
                            changeChain={setChain}
                            jobs={jobs}
                            selectedJob={selectedJob}
                            currentJob={job}
                        />
                    }
                </Stack>
            </CardContent>
        </Card>
    )
}

SchedulePanel.propTypes = {
    job: PropTypes.instanceOf(Job).isRequired,
    jobs: arrayOf(Job),
    onChangePlanification: PropTypes.func.isRequired,
    onChangeEmails: PropTypes.func.isRequired,
    onChangeContactsIds: PropTypes.func.isRequired,
    isEditMode: PropTypes.bool,
    testMail: PropTypes.func,
    applicationSettings: arrayOf(SieauParameterDto),
}
export default SchedulePanel
